import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import {
  isSameDay,
  isWithinInterval,
  isBefore,
  isAfter,
  set,
  addMonths as addMonthsFn,
  differenceInMonths,
} from "date-fns";
import React, { useState, useCallback, useMemo } from "react";

import Button, { ButtonVariantEnum } from "@/components/atoms/forms/Button";
import { SizeEnum } from "@/enums";

type DisabledConfig =
  | boolean
  | Date
  | Date[]
  | { from: Date; to: Date }
  | { dayOfWeek: number[] }
  | { before?: Date; after?: Date };

function isDateDisabled(
  day: DayObj,
  disabled: DisabledConfig | undefined,
): boolean {
  if (!disabled) return false;

  const date = set(new Date(), {
    year: day.year,
    month: day.month,
    date: day.day,
  });

  if (disabled === true) return true;

  if (disabled instanceof Date) {
    return isSameDay(date, disabled);
  }

  if (Array.isArray(disabled)) {
    return disabled.some((d) => isSameDay(date, d));
  }

  if (typeof disabled === "object") {
    if ("from" in disabled && "to" in disabled) {
      return isWithinInterval(date, { start: disabled.from, end: disabled.to });
    }

    if ("dayOfWeek" in disabled && Array.isArray(disabled.dayOfWeek)) {
      return disabled.dayOfWeek.includes(date.getDay());
    }

    const { before, after } = disabled as { before?: Date; after?: Date };

    if (before && isBefore(date, before)) return true;
    if (after && isAfter(date, after)) return true;
  }

  return false;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MAX_MONTH_DISPLAY = 12;
const MONTH_WINDOW_CAROUSEL = 2;

interface MonthBase {
  year: number;
  month: number;
}

interface DayObj {
  day: number;
  month: number;
  year: number;
  ts: number;
}

interface MonthGridProps {
  year: number;
  month: number;
  today: DayObj;
  selected: DayObj | null;
  disabled: DisabledConfig | undefined;
  hiddenDaybar?: boolean;
  hideMonthLabel?: boolean;
  onDayClick: (day: DayObj) => void;
}

function addMonths(base: MonthBase, n: number): MonthBase {
  const date = new Date(base.year, base.month, 1);
  const newDate = addMonthsFn(date, n);
  return { year: newDate.getFullYear(), month: newDate.getMonth() };
}

function monthsSince(a: MonthBase, b: MonthBase): number {
  const dateA = new Date(a.year, a.month, 1);
  const dateB = new Date(b.year, b.month, 1);
  return differenceInMonths(dateA, dateB);
}

function toTs(y: number, m: number, d: number): number {
  return new Date(y, m, d).getTime();
}

function MonthGrid({
  year,
  month,
  today,
  selected,
  disabled,
  hiddenDaybar,
  hideMonthLabel,
  onDayClick,
}: MonthGridProps) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const getDayState = (d: number) => {
    const ts = toTs(year, month, d);
    const isToday =
      d === today.day && month === today.month && year === today.year;
    const isSel = selected?.ts === ts;
    const isDisabled = isDateDisabled({ day: d, month, year, ts }, disabled);
    return { ts, isToday, isSel, isDisabled };
  };

  const firstEmptyDay = Array.from({ length: firstDay }).map((_, i) => (
    <div key={`empty-start-${i}`} />
  ));
  const total = firstDay + daysInMonth;
  const trailing = (7 - (total % 7)) % 7;
  const trailingEmptyDay = Array.from({ length: trailing }).map((_, i) => (
    <div key={`empty-end-${i}`} />
  ));

  const dayCells: React.ReactNode[] = Array.from({
    length: daysInMonth,
  }).map((_, i) => {
    const { ts, isSel, isDisabled } = getDayState(i + 1);

    return (
      <div
        key={`${year}-${month}-${i + 1}`}
        className={clsx(
          "relative flex h-10 w-10 items-center justify-center rounded-sm text-sm text-slate-700 transition-colors duration-100 select-none",
          !isDisabled && "cursor-pointer",
          isDisabled && "cursor-not-allowed text-slate-400 opacity-50",
          isSel && "bg-jade-600 text-white",
        )}
        onClick={() =>
          !isDisabled && onDayClick({ day: i + 1, month, year, ts })
        }
      >
        <span>{i + 1}</span>
      </div>
    );
  });

  return (
    <div className="w-full">
      {!hiddenDaybar && (
        <div className="grid shrink-0 grid-cols-7 bg-white py-2">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-sm text-slate-400">
              {d}
            </div>
          ))}
        </div>
      )}

      {!hideMonthLabel && (
        <p className="text-text-primary py-1 text-left font-bold text-shadow-none">
          {MONTHS[month]} {year}
        </p>
      )}

      <div className="grid grid-cols-7">
        {firstEmptyDay}
        {dayCells}
        {trailingEmptyDay}
      </div>
    </div>
  );
}

interface Props {
  onSelectDate?: (date: Date) => void;
  disabled?: DisabledConfig;
  hideNavigation?: boolean;
  hideMonthLabel?: boolean;
  stickyDaybar?: boolean;
  variant?: "scroll" | "carousel";
}

export default function Calendar({
  disabled,
  hideNavigation,
  stickyDaybar,
  onSelectDate,
  variant = "scroll",
  hideMonthLabel,
}: Props) {
  const monthWindow =
    variant === "carousel" ? MONTH_WINDOW_CAROUSEL : MAX_MONTH_DISPLAY;

  const today = useMemo<DayObj>(() => {
    const now = new Date();
    return {
      day: now.getDate(),
      month: now.getMonth(),
      year: now.getFullYear(),
      ts: toTs(now.getFullYear(), now.getMonth(), now.getDate()),
    };
  }, []);

  const maxBase = useMemo(
    () => addMonths(today, MAX_MONTH_DISPLAY - monthWindow),
    [today, monthWindow],
  );

  const [base, setBase] = useState<MonthBase>({
    year: today.year,
    month: today.month,
  });
  const [selected, setSelected] = useState<DayObj | null>(null);

  const endBase = useMemo(
    () => addMonths(base, monthWindow - 1),
    [base, monthWindow],
  );

  const navigate = useCallback(
    (dir: "next" | "prev") => {
      setBase((b) => {
        const newBase = addMonths(
          b,
          dir === "next" ? monthWindow : -monthWindow,
        );

        return newBase;
      });
    },
    [monthWindow],
  );

  const handleDayClick = useCallback(
    (day: DayObj) => {
      setSelected(day);
      onSelectDate && onSelectDate(new Date(day.year, day.month, day.day));
    },
    [onSelectDate],
  );

  const monthList = useMemo(
    () =>
      Array.from({ length: monthWindow }, (_, i) => addMonths(base, i)).map(
        (m) => (
          <MonthGrid
            key={`${m.year}-${m.month}`}
            year={m.year}
            month={m.month}
            today={today}
            selected={selected}
            disabled={disabled}
            hiddenDaybar={stickyDaybar}
            hideMonthLabel={hideMonthLabel}
            onDayClick={handleDayClick}
          />
        ),
      ),
    [
      base,
      monthWindow,
      today,
      selected,
      disabled,
      stickyDaybar,
      hideMonthLabel,
      handleDayClick,
    ],
  );

  return (
    <div className="mx-auto w-full max-w-sm">
      <div className="flex flex-col overflow-hidden bg-white">
        {!hideNavigation && variant === "carousel" && (
          <div className="grid grid-cols-2">
            <div className="flex items-center">
              <Button
                className="px-2 py-0"
                icon={faChevronLeft}
                variant={ButtonVariantEnum.PLAIN}
                onClick={() => navigate("prev")}
                size={SizeEnum.SMALL}
                disabled={monthsSince(base, today) <= 0}
              />
              <span className="text-text-primary mx-auto font-bold">
                {MONTHS[base.month].slice(0, 3)} {base.year}
              </span>
            </div>

            <div className="flex items-center justify-end">
              <span className="text-text-primary mx-auto font-bold">
                {MONTHS[endBase.month].slice(0, 3)} {endBase.year}
              </span>
              <Button
                className="px-2 py-0"
                icon={faChevronRight}
                variant={ButtonVariantEnum.PLAIN}
                onClick={() => navigate("next")}
                size={SizeEnum.SMALL}
                disabled={
                  monthsSince(addMonths(base, monthWindow), maxBase) > 0
                }
              />
            </div>
          </div>
        )}

        {stickyDaybar && (
          <div className="grid shrink-0 grid-cols-7 border-b border-slate-100 bg-white px-5 py-2">
            {DAYS.map((d) => (
              <div key={d} className="text-text-secondary text-center text-xs">
                {d}
              </div>
            ))}
          </div>
        )}

        {variant === "scroll" ? (
          <div className="max-h-[calc(100dvh-90px)] overflow-y-auto px-5 pb-4">
            {monthList}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 overflow-x-auto">
            {monthList}
          </div>
        )}
      </div>
    </div>
  );
}
